import React from 'react';
import { Link } from 'react-router';
import { AvatarWithFlag } from 'components/AvatarWithFlag/AvatarWithFlag';
import UsernameLink from 'components/UsernameLink/UsernameLink';
import { formatRelativeTime } from 'utils/relativeTime';
import { FeedEventActor } from 'types/interfaces/followingFeed';
import './FeedCardShell.css';

export interface FeedCardShellActor {
  username: string;
  slug: string | null;
  avatarUrl: string | null;
  isElite?: boolean;
  /** Optional user preference override — when omitted, Elite users show the animated border by default. */
  showAnimatedBorder?: boolean;
}

const profileHref = (actor: FeedCardShellActor) =>
  actor.slug ? `/u/${actor.slug}` : `/profile/u/${actor.username}`;

interface ActorCellProps {
  actor: FeedCardShellActor;
  size: number;
}

/**
 * Avatar + username pair. Avatar is wrapped in a plain Link (navigation only),
 * username uses UsernameLink which handles the trophy-case hover overlay and
 * EliteBadge — same treatment used on leaderboard and profile links elsewhere.
 * Two separate links avoids nested-anchor invalid HTML.
 */
const ActorCell: React.FC<ActorCellProps> = ({ actor, size }) => {
  const showAnimatedBorder =
    actor.showAnimatedBorder ?? !!actor.isElite;
  return (
    <span className="feed-card-shell__actor">
      <Link
        to={profileHref(actor)}
        className="feed-card-shell__avatar-link"
        aria-label={`${actor.username}'s profile`}
      >
        <AvatarWithFlag
          avatarUrl={actor.avatarUrl}
          username={actor.username}
          countryCode={null}
          size={size}
          isElite={!!actor.isElite}
          showAnimatedBorder={showAnimatedBorder}
        />
      </Link>
      <UsernameLink
        username={actor.username}
        isElite={!!actor.isElite}
        className="feed-card-shell__actor-name"
      />
    </span>
  );
};

interface FeedCardShellProps {
  variant?: 'default' | 'milestone';
  actor: FeedCardShellActor;
  actionText: React.ReactNode;
  createdAt: string;
  secondaryActor?: FeedCardShellActor;
  /** Inline copy rendered between primary and secondary avatars (e.g., "traded cards to"). */
  connectorText?: React.ReactNode;
  hero?: React.ReactNode;
  subline?: React.ReactNode;
  footer?: React.ReactNode;
  belowFooter?: React.ReactNode;
}

export const FeedCardShell: React.FC<FeedCardShellProps> = ({
  variant = 'default',
  actor,
  actionText,
  createdAt,
  secondaryActor,
  connectorText,
  hero,
  subline,
  footer,
  belowFooter,
}) => {
  const hasFooter = !!footer;
  const shellClass = [
    'feed-card-shell',
    `feed-card-shell--${variant}`,
    hasFooter ? '' : 'feed-card-shell--no-footer',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <article className={shellClass}>
      <header className="feed-card-shell__header">
        <ActorCell actor={actor} size={36} />

        {secondaryActor ? (
          <>
            {connectorText ? (
              <span className="feed-card-shell__connector">
                {connectorText}
              </span>
            ) : null}
            <ActorCell actor={secondaryActor} size={32} />
          </>
        ) : (
          <span className="feed-card-shell__action">{actionText}</span>
        )}

        <span className="feed-card-shell__time">
          {formatRelativeTime(createdAt)}
        </span>
      </header>

      {hero ? (
        <div className="feed-card-shell__hero">
          <div className="feed-card-shell__gradient" aria-hidden />
          <div className="feed-card-shell__hero-content">{hero}</div>
        </div>
      ) : null}

      {subline ? <div className="feed-card-shell__subline">{subline}</div> : null}

      {footer ? <div className="feed-card-shell__footer">{footer}</div> : null}

      {belowFooter ?? null}
    </article>
  );
};

export { profileHref };
export type { FeedEventActor };
export default FeedCardShell;
